import { useState } from 'react';
import { User, Mail, Phone, Lock, Eye, EyeOff, UserPlus, AlertTriangle, CheckCircle, X, AlertCircle } from 'lucide-react';
import { useAdminRegistration } from '../../authentication_components/adminRegistrationContext';

export default function AdminRegistration() {
  const [adminData, setAdminData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Use the admin registration context
  const {
    createAdmin,
    loading,
    error,
    clearError,
    validateAdminData,
    validatePassword,
    isMasterUser
  } = useAdminRegistration();

  // If not master user, show access denied
  if (!isMasterUser) {
    return (
      <div className="bg-gradient-to-br from-red-50 to-orange-50 min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 border border-red-200">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Acceso Restringido
            </h2>
            <p className="text-gray-600 mb-6">
              Solo usuarios maestros pueden registrar nuevos administradores.
            </p>
            <button
              onClick={() => window.history.back()}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Volver Atrás
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Clear any global error when user starts typing
    if (error) {
      clearError();
    }
    
    // Format phone number
    let newValue = value;
    if (name === 'phone') {
      // Remove all non-digits
      const digits = value.replace(/\D/g, '');
      // Format as (XXX) XXX-XXXX
      if (digits.length <= 3) {
        newValue = digits;
      } else if (digits.length <= 6) {
        newValue = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      } else {
        newValue = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
      }
    }
    
    // Only allow letters and spaces for names
    if ((name === 'firstName' || name === 'lastName') && value !== '') {
      const letterPattern = /^[a-zA-ZáéíóúñÑüÜ\s]*$/;
      if (!letterPattern.test(value)) {
        return;
      }
    }

    setAdminData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Clear field-specific error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // First name validation
    if (!adminData.firstName.trim()) {
      newErrors.firstName = 'El nombre es requerido';
    } else if (adminData.firstName.trim().length < 2) {
      newErrors.firstName = 'El nombre debe tener al menos 2 caracteres';
    }

    // Last name validation
    if (!adminData.lastName.trim()) {
      newErrors.lastName = 'El apellido es requerido';
    } else if (adminData.lastName.trim().length < 2) {
      newErrors.lastName = 'El apellido debe tener al menos 2 caracteres';
    }

    // Email validation
    if (!adminData.email.trim()) {
      newErrors.email = 'El correo electrónico es requerido';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(adminData.email)) {
        newErrors.email = 'Formato de correo electrónico inválido';
      }
    }

    // Phone validation
    if (!adminData.phone.trim()) {
      newErrors.phone = 'El número de teléfono es requerido';
    } else {
      const phoneDigits = adminData.phone.replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        newErrors.phone = 'El número de teléfono debe tener 10 dígitos';
      }
    }

    // Password validation using context validator
    const passwordErrors = validatePassword(adminData.password);
    if (passwordErrors.length > 0) {
      newErrors.password = passwordErrors[0]; // Show first error
    }

    // Confirm password validation
    if (!adminData.confirmPassword) {
      newErrors.confirmPassword = 'Debe confirmar la contraseña';
    } else if (adminData.password !== adminData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      setShowConfirmDialog(true);
    }
  };

  const confirmRegistration = async () => {
    try {
      // Prepare admin data for API
      const adminPayload = {
        admin_first_name: adminData.firstName.trim(),
        admin_last_name: adminData.lastName.trim(),
        admin_email: adminData.email.trim(),
        admin_phone: adminData.phone.replace(/\D/g, ''), // Send only digits
        admin_password: adminData.password,
        access_status: 'pending' // Default status
      };

      // Validate using context validator
      const validationErrors = validateAdminData(adminPayload);
      if (validationErrors.length > 0) {
        const errorObj = {};
        validationErrors.forEach(err => {
          errorObj[err.field] = err.message;
        });
        setErrors(errorObj);
        setShowConfirmDialog(false);
        return;
      }

      // Call the API
      const result = await createAdmin(adminPayload);
      
      if (result.success) {
        setSuccessMessage(`¡Administrador registrado exitosamente!\nID: ${result.admin_id}\nEstado: ${result.admin?.admin_access_status || 'Pendiente'}`);
        
        // Reset form
        setAdminData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          password: '',
          confirmPassword: ''
        });
        
        setShowConfirmDialog(false);
        setErrors({});
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      }
    } catch (err) {
      console.error('Error registering admin:', err);
      
      // Handle specific API errors
      if (err.message.includes('already registered') || err.message.includes('already exists')) {
        setErrors({ email: 'Este correo electrónico ya está registrado' });
      } else if (err.message.includes('validation')) {
        // Handle backend validation errors
        setErrors({ submit: 'Error de validación. Verifique los datos ingresados.' });
      } else {
        setErrors({ submit: err.message || 'Error al registrar el administrador. Intente nuevamente.' });
      }
      
      setShowConfirmDialog(false);
    }
  };

  const cancelRegistration = () => {
    setShowConfirmDialog(false);
  };

  const dismissSuccess = () => {
    setSuccessMessage('');
  };

  return (
    <div className="bg-gradient-to-br from-orange-50 to-orange-100 min-h-screen">
      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 max-w-md bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg">
          <div className="flex items-start">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-green-800 font-medium text-sm">Registro Exitoso</p>
              <p className="text-green-700 text-xs mt-1 whitespace-pre-line">{successMessage}</p>
            </div>
            <button
              onClick={dismissSuccess}
              className="text-green-400 hover:text-green-600 ml-2"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Global Error Message */}
      {error && (
        <div className="fixed top-4 right-4 z-50 max-w-md bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-red-800 font-medium text-sm">Error</p>
              <p className="text-red-700 text-xs mt-1">{error}</p>
            </div>
            <button
              onClick={clearError}
              className="text-red-400 hover:text-red-600 ml-2"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-orange-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center">
            <UserPlus className="h-6 w-6 text-orange-500 mr-3" />
            Registrar Nuevo Administrador
          </h2>
          <p className="text-gray-600 mt-2">
            Complete todos los campos para registrar un nuevo administrador en el sistema
          </p>
        </div>
      </div>

      {/* Main Form */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-xl border border-orange-200 p-8">
          {/* Submit Error */}
          {errors.submit && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-red-800 text-sm">{errors.submit}</p>
              </div>
            </div>
          )}

          <div className="space-y-6">
            
            {/* Personal Information Section */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                Información Personal
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-orange-400" />
                    </div>
                    <input
                      type="text"
                      name="firstName"
                      value={adminData.firstName}
                      onChange={handleChange}
                      disabled={loading}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 disabled:opacity-50 disabled:bg-gray-50 ${
                        errors.firstName ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Ingrese el nombre"
                    />
                  </div>
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Apellido *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-orange-400" />
                    </div>
                    <input
                      type="text"
                      name="lastName"
                      value={adminData.lastName}
                      onChange={handleChange}
                      disabled={loading}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 disabled:opacity-50 disabled:bg-gray-50 ${
                        errors.lastName ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Ingrese el apellido"
                    />
                  </div>
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                Información de Contacto
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Correo Electrónico *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-orange-400" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={adminData.email}
                      onChange={handleChange}
                      disabled={loading}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 disabled:opacity-50 disabled:bg-gray-50 ${
                        errors.email ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="admin@jaydevspr.com"
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Número de Teléfono *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-orange-400" />
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      value={adminData.phone}
                      onChange={handleChange}
                      maxLength="14"
                      disabled={loading}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 disabled:opacity-50 disabled:bg-gray-50 ${
                        errors.phone ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="(787) 555-0123"
                    />
                  </div>
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Security Information Section */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                Credenciales de Acceso
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contraseña *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-orange-400" />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={adminData.password}
                      onChange={handleChange}
                      disabled={loading}
                      className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 disabled:opacity-50 disabled:bg-gray-50 ${
                        errors.password ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-orange-400 hover:text-orange-600 transition-colors" />
                      ) : (
                        <Eye className="h-5 w-5 text-orange-400 hover:text-orange-600 transition-colors" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Debe contener al menos 8 caracteres, mayúscula, minúscula, número y carácter especial
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmar Contraseña *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-orange-400" />
                    </div>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={adminData.confirmPassword}
                      onChange={handleChange}
                      disabled={loading}
                      className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 disabled:opacity-50 disabled:bg-gray-50 ${
                        errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={loading}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5 text-orange-400 hover:text-orange-600 transition-colors" />
                      ) : (
                        <Eye className="h-5 w-5 text-orange-400 hover:text-orange-600 transition-colors" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6 border-t border-gray-200">
              <div className="space-y-3">
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-black to-gray-800 hover:from-gray-800 hover:to-black text-white py-3 px-6 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                      Registrando...
                    </div>
                  ) : (
                    'Registrar Administrador'
                  )}
                </button>
                
                {/* History Button */}
                <button
                  onClick={() => window.location.href = '/admin-registration-history'}
                  disabled={loading}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Ver Historial de Registros
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-800 flex items-center">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
                  Confirmar Registro de Administrador
                </h3>
                <button
                  onClick={cancelRegistration}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={loading}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
                  <div>
                    <p className="text-yellow-800 font-medium">
                      Confirmación Requerida
                    </p>
                    <p className="text-yellow-700 text-sm mt-1">
                      Verifique toda la información antes de proceder. El administrador será creado con estado "pendiente" y requerirá aprobación.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4 mb-6">
                <h4 className="font-medium text-gray-800">Información del Administrador:</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-600">Nombre:</span>
                    <p className="text-gray-800">{adminData.firstName} {adminData.lastName}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Email:</span>
                    <p className="text-gray-800">{adminData.email}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Teléfono:</span>
                    <p className="text-gray-800">{adminData.phone}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Estado Inicial:</span>
                    <p className="text-yellow-600 font-medium">Pendiente de Aprobación</p>
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={cancelRegistration}
                  disabled={loading}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmRegistration}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Registrando...
                    </div>
                  ) : (
                    'Confirmar Registro'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}